package dev.kuml.portal.web.auth

import dev.kuml.portal.web.AppScope
import dev.kuml.portal.web.api.ApiResult
import dev.kuml.portal.web.api.PortalApi
import dev.kuml.portal.web.components.languageSwitcher
import dev.kuml.portal.web.components.legalLinks
import dev.kuml.portal.web.i18n.t
import io.kvision.core.Container
import io.kvision.form.text.TextInput
import io.kvision.form.text.textInput
import io.kvision.html.Button
import io.kvision.html.Div
import io.kvision.html.InputType
import io.kvision.html.button
import io.kvision.html.div
import io.kvision.html.image
import kotlinx.coroutines.launch

/** Server error code for an unknown/expired/already-consumed reset token -- mirrors
 * `dev.kuml.portal.server.auth.passwordResetRoutes`' `"reset_token_invalid"` literal, see
 * `PasswordResetService.ConfirmResult.NotFound`'s KDoc. */
private const val RESET_TOKEN_INVALID_CODE = "reset_token_invalid"

/**
 * R-4: the landing page a clicked `{baseUrl}/reset-password/{token}` link from
 * `dev.kuml.portal.server.email.EmailService.sendPasswordReset` actually resolves to via
 * [dev.kuml.portal.web.routing.Router]'s `/reset-password/:token` route -- the ONLY caller of
 * [PortalApi.confirmPasswordReset]. Unauthenticated, same shell/footer as [authScreen]: a reset
 * link is followed straight from an email client, often with no session at all.
 *
 * ## Two different failure shapes
 * A [RESET_TOKEN_INVALID_CODE] rejection (token unknown/expired/already consumed,
 * `PasswordResetService.ConfirmResult.NotFound`) is TERMINAL -- the form is torn down and replaced
 * by a dead-end message plus a [dev.kuml.portal.web.i18n.strings.AuthStrings.requestNewResetLink]
 * button, because resubmitting the SAME token can never succeed. A password-SHAPE rejection
 * (`ConfirmResult.Rejected`, e.g. `password_too_short`) instead re-enables the SAME form in place --
 * the token itself is still valid, only the chosen password was not, so the caller just needs to
 * pick a different one and resubmit.
 */
public fun Container.resetPasswordScreen(
    api: PortalApi,
    token: String,
    onImpressumClick: () -> Unit,
    onDatenschutzClick: () -> Unit,
    onAgbClick: () -> Unit,
    onRequestNewLink: () -> Unit,
    onDone: () -> Unit,
) {
    lateinit var cardBody: Div

    fun renderTerminal(
        title: String,
        message: String,
        actionLabel: String,
        onAction: () -> Unit,
    ) {
        cardBody.removeAll()
        cardBody.div(title, className = "kportal-auth-title")
        cardBody.div(message, className = "kportal-auth-message")
        cardBody.div(className = "kportal-auth-actions") {
            button(actionLabel, className = "kportal-btn kportal-btn-primary kportal-btn-auto").onClick { onAction() }
        }
    }

    fun renderForm() {
        cardBody.removeAll()
        cardBody.div(t.auth.resetPasswordTitle, className = "kportal-auth-title")

        lateinit var passwordInput: TextInput
        lateinit var passwordError: Div
        lateinit var confirmPasswordInput: TextInput
        lateinit var confirmPasswordError: Div
        lateinit var submitButton: Button
        lateinit var statusDiv: Div
        var submitting = false

        fun validatePassword(): Boolean {
            val value = passwordInput.value.orEmpty()
            // Always enforced (unlike AuthScreen.validatePassword's LOGIN-mode exemption) -- this
            // screen only ever CHANGES a password, exactly like AuthScreen's REGISTER path, which
            // is why it shares that path's [MIN_PASSWORD_LENGTH] constant.
            val tooShort = value.isNotEmpty() && value.length < MIN_PASSWORD_LENGTH
            val valid = value.isNotEmpty() && !tooShort
            passwordError.content =
                when {
                    valid -> ""
                    value.isEmpty() -> t.auth.passwordRequired
                    else -> t.auth.passwordTooShort
                }
            if (valid) passwordInput.removeCssClass("kportal-input-error") else passwordInput.addCssClass("kportal-input-error")
            return valid
        }

        fun validateConfirmPassword(): Boolean {
            val value = confirmPasswordInput.value.orEmpty()
            val valid = value.isNotEmpty() && value == passwordInput.value.orEmpty()
            confirmPasswordError.content = if (valid) "" else t.auth.passwordsDoNotMatch
            if (valid) {
                confirmPasswordInput.removeCssClass("kportal-input-error")
            } else {
                confirmPasswordInput.addCssClass("kportal-input-error")
            }
            return valid
        }

        fun setSubmitting(value: Boolean) {
            submitting = value
            submitButton.disabled = value
            submitButton.text = if (value) t.auth.submitResetPasswordBusy else t.auth.submitResetPassword
        }

        fun setStatus(message: String) {
            statusDiv.content = message
            if (message.isEmpty()) {
                statusDiv.removeCssClass("kportal-status-error")
                statusDiv.addCssClass("kportal-status-neutral")
            } else {
                statusDiv.addCssClass("kportal-status-error")
                statusDiv.removeCssClass("kportal-status-neutral")
            }
        }

        fun submit() {
            if (submitting) return
            val passwordOk = validatePassword()
            val confirmOk = validateConfirmPassword()
            if (!passwordOk || !confirmOk) return

            val password = passwordInput.value!!
            setSubmitting(true)
            AppScope.launch {
                when (val result = api.confirmPasswordReset(token, password)) {
                    is ApiResult.Success ->
                        renderTerminal(
                            t.auth.resetPasswordSuccessTitle,
                            t.auth.resetPasswordSuccessMessage,
                            t.auth.continueToLogin,
                            onDone,
                        )
                    is ApiResult.HttpError -> {
                        if (result.error?.code == RESET_TOKEN_INVALID_CODE) {
                            renderTerminal(
                                t.auth.resetPasswordFailureTitle,
                                t.auth.resetPasswordFailureMessage,
                                t.auth.requestNewResetLink,
                                onRequestNewLink,
                            )
                        } else {
                            setSubmitting(false)
                            setStatus(t.errors.forCode(result.error?.code, result.status) ?: t.auth.resetPasswordFailureMessage)
                        }
                    }
                    ApiResult.NetworkError -> {
                        setSubmitting(false)
                        setStatus(t.common.networkErrorRetry)
                    }
                }
            }
        }

        cardBody.div(className = "kportal-field") {
            div(t.auth.newPasswordLabel, className = "kportal-label")
            passwordInput = textInput(type = InputType.PASSWORD, className = "kportal-input")
            passwordError = div(className = "kportal-field-error")
        }
        cardBody.div(className = "kportal-field") {
            div(t.auth.confirmPasswordLabel, className = "kportal-label")
            confirmPasswordInput = textInput(type = InputType.PASSWORD, className = "kportal-input")
            confirmPasswordError = div(className = "kportal-field-error")
        }
        submitButton = cardBody.button(t.auth.submitResetPassword, className = "kportal-btn kportal-btn-primary")
        submitButton.onClick { submit() }
        statusDiv = cardBody.div(className = "kportal-status-area") { setAttribute("aria-live", "polite") }

        passwordInput.setEventListener<TextInput> {
            blur = { validatePassword() }
            keydown = { e ->
                if (e.key == "Enter") {
                    e.preventDefault()
                    submit()
                }
            }
        }
        confirmPasswordInput.setEventListener<TextInput> {
            blur = { validateConfirmPassword() }
            keydown = { e ->
                if (e.key == "Enter") {
                    e.preventDefault()
                    submit()
                }
            }
        }
    }

    div(className = "kportal-auth-shell") {
        div(className = "kportal-auth-card") {
            div(className = "kportal-wordmark") {
                image(src = "img/logo.svg", alt = "kUML").addCssClass("kportal-logo-light")
                image(src = "img/logo-negative.svg", alt = "kUML").addCssClass("kportal-logo-dark")
            }
            cardBody = div()
        }
        div(className = "kportal-auth-footer") {
            legalLinks(onImpressumClick, onDatenschutzClick, onAgbClick)
            languageSwitcher()
        }
    }

    renderForm()
}
